import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import logger from "@/lib/logger";
import { useAuth } from "@/context/AuthContext";
import { StatCard } from "@/components/Layout";
import TodayBanner from "@/components/TodayBanner";
import CelebrationHero from "@/components/CelebrationHero";
import usePermissions from "@/lib/usePermissions";
import { toast } from "sonner";
import TopPostsFeed from "@/components/TopPostsFeed";
import InspirationFeed from "@/components/InspirationFeed";
import { invoiceRevenueINR, matchesMonth, monthWindow, isCountableExpense, expenseAmountINR } from "@/lib/finance";
import { bigDateLabel } from "@/lib/dashboardHelpers";
import TabSection from "@/components/dashboard/TabSection";
import DashboardStickyHeader from "@/components/dashboard/DashboardStickyHeader";
import DashboardOverviewTab from "@/components/dashboard/DashboardOverviewTab";
import DashboardPeopleTab from "@/components/dashboard/DashboardPeopleTab";
import DashboardProjectsTab from "@/components/dashboard/DashboardProjectsTab";
import DashboardFinanceTab from "@/components/dashboard/DashboardFinanceTab";

// ----------------------------------------------------------------------------
// Phase 46 — Dashboard tabs
// Phase 147 — Dashboard Cleanup: this page now only owns data-fetching state
// and the composition of its 4 tabs (see components/dashboard/*). Pure JSX
// blocks and static helpers were extracted 1:1, no behavior changes.
// ----------------------------------------------------------------------------

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const can = usePermissions();
  // Feb 2026 — no single blanket "isAdmin"; each widget checks its own
  // matrix feature. `isAdmin` remains as a light cosmetic flag (subtitle
  // copy, "see everything" project grouping) — real data-fetching gates
  // below use the specific feature.
  const isAdmin = can("team_admin") || can("leaves_edit") || can("attendance_admin");
  const canSeeAllLeaveBalances = can("leaves_edit");
  const canSeeAttendanceInsights = can("attendance_admin");

  const [today, setToday] = useState(null);
  const [projects, setProjects] = useState([]);
  const [milestonesByProject, setMilestonesByProject] = useState({});
  const [leaves, setLeaves] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [team, setTeam] = useState([]);
  const [balance, setBalance] = useState(null);
  const [allBalances, setAllBalances] = useState(null);
  const [goals, setGoals] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [openTasks, setOpenTasks] = useState([]);
  const [celebrations, setCelebrations] = useState(null);
  // Feb 2026 — the "Open reviews" tile must reflect what the CURRENT user
  // can actually decide on (matches the Approvals inbox exactly). Sourced
  // from /api/approvals/counts which mirrors the inbox visibility rules,
  // instead of a global reviews.filter() that ignored RBAC.
  const [approvalCounts, setApprovalCounts] = useState({ pending: {}, approved: {} });

  // Phase 46 — tab + finance state
  const [activeTab, setActiveTab] = useState("overview");
  // Phase 153 — Inspiration Feed "Turn into Post" hand-off signal: switches
  // Home to the Overview tab and bumps the Task Hub straight to Social Posts
  // once the article has been turned into a real, Idea-status draft.
  const [socialTabSignal, setSocialTabSignal] = useState(null); // {ts}
  const handleInspirationPostCreated = () => {
    setActiveTab("overview");
    setSocialTabSignal({ ts: Date.now() });
    setTimeout(() => document.querySelector('[data-testid="task-hub-tab-social"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };
  const [taskStatusFilter, setTaskStatusFilter] = useState(null);   // Phase 105 — Scorecard drill-down
  const [taskPersonId, setTaskPersonId] = useState(null);           // Phase 120 — Master Filter Bar
  const [taskProjectId, setTaskProjectId] = useState(null);         // Phase 120 — Master Filter Bar
  const [taskBusinessUnit, setTaskBusinessUnit] = useState(null);   // Phase 120 — Master Filter Bar
  // Phase 120 — default the Team Member filter to "Myself" once the user
  // loads, so the landing view stays personalized without a Mine/Team toggle.
  useEffect(() => {
    if (user?.id) setTaskPersonId((prev) => (prev === null ? user.id : prev));
  }, [user?.id]);
  const [financeMonth, setFinanceMonth] = useState("all");
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]); // snapshot, not month-filtered
  const [financeLoaded, setFinanceLoaded] = useState(false);
  // Phase 93 — Studio Runway (Phase 94: Cash Flow + Revenue by Service are
  // now derived client-side via useMemo below, driven by the Month Selector)
  const [runway, setRunway] = useState(null);
  // Phase 93 — Monthly Late Count (People tab)
  const [lateCounts, setLateCounts] = useState(null);
  const [lateCountsLoaded, setLateCountsLoaded] = useState(false);
  // Daily gratitude — today's entry for the current user (gates Check-out for non-founders).
  const [gratitudeToday, setGratitudeToday] = useState(null);
  // LinkedIn rotation — today's assignment + my post (gates Check-out for the assignee).
  const [linkedinToday, setLinkedinToday] = useState(null);
  const isFounder = user?.role === "founder";

  const loadAll = async () => {
    try {
      const reqs = [
        api.get("/attendance/today").catch(() => ({ data: null })),
        api.get("/projects").catch(() => ({ data: [] })),
        api.get("/leaves").catch(() => ({ data: [] })),
        api.get("/purchase-requests").catch(() => ({ data: [] })),
        api.get("/reviews").catch(() => ({ data: [] })),
        api.get("/users").catch(() => ({ data: [] })),
        api.get("/leaves/balance").catch(() => ({ data: null })),
        api.get("/goals").catch(() => ({ data: [] })),
        api.get("/achievements").catch(() => ({ data: [] })),
        api.get("/reviews/my-open-tasks").catch(() => ({ data: [] })),
        api.get("/dashboard/celebrations").catch(() => ({ data: null })),
        api.get("/approvals/counts").catch(() => ({ data: { pending: {}, approved: {} } })),
        api.get("/gratitude/today").catch(() => ({ data: null })),
        api.get("/linkedin/today").catch(() => ({ data: null })),
      ];
      const [t, p, l, pu, r, u, b, g, a, ot, c, ac, gr, lk] = await Promise.all(reqs);
      setToday(t.data && t.data.id ? t.data : null);
      setProjects(p.data);
      setLeaves(l.data);
      setPurchases(pu.data);
      setReviews(r.data);
      setTeam(u.data);
      setBalance(b.data);
      setGoals(g.data);
      setAchievements(a.data);
      setOpenTasks(ot.data);
      setCelebrations(c.data);
      setApprovalCounts(ac.data || { pending: {}, approved: {} });
      setGratitudeToday(gr?.data || null);
      setLinkedinToday(lk?.data || null);
      // Pull milestones for each visible project (cap at 6 most relevant)
      const visible = p.data.slice(0, 8);
      const ms = await Promise.all(visible.map((pp) =>
        api.get(`/projects/${pp.id}/milestones`).then((rr) => [pp.id, rr.data]).catch(() => [pp.id, []])
      ));
      setMilestonesByProject(Object.fromEntries(ms));
    } catch (err) {
      logger.warn("Dashboard load failed:", err?.message || err);
    }
  };

  useEffect(() => {
    loadAll();
    if (canSeeAllLeaveBalances) api.get("/leaves/balance/all").then((r) => setAllBalances(r.data)).catch(() => {});
  }, [canSeeAllLeaveBalances]);

  const checkIn = async () => {
    try { await api.post("/attendance/checkin"); toast.success("Checked in"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const submitGratitude = async (text, imageUrl) => {
    try {
      const { data } = await api.post("/gratitude", { entry_text: text, image_url: imageUrl || null });
      setGratitudeToday(data);
      toast.success("Gratitude noted — have a great day!");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reloadLinkedin = async () => {
    try { const { data } = await api.get("/linkedin/today"); setLinkedinToday(data || null); }
    catch { /* keep prior state */ }
  };
  const checkOut = async () => {
    try { await api.post("/attendance/checkout"); toast.success("Checked out — see you tomorrow"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  // Phase 84 — clear a check-out timestamp that was set incorrectly.
  const undoCheckOut = async () => {
    if (!window.confirm("Clear today's check-out timestamp?")) return;
    try { await api.post("/attendance/checkout/undo"); toast.success("Check-out cleared"); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // --- Stat tile values ---
  // Internal staff = not a client/vendor party, regardless of role/kind field.
  const isInternalStaff = (u) =>
    u.role !== "client" && u.role !== "vendor" && u.kind !== "client" && u.kind !== "vendor";
  const activeProjects = projects.filter((p) => p.status === "active").length;
  const completedProjects = projects.filter((p) => p.status === "completed").length;
  const allMilestones = Object.values(milestonesByProject).flat();
  const openMilestones = allMilestones.filter((m) => m.status !== "done").length;
  const pendingLeaves = leaves.filter((l) => l.status === "pending");
  const pendingPurchases = purchases.filter((x) => x.status === "pending");
  const openReviews = reviews.filter((r) => r.status === "pending" || r.status === "changes_requested");
  // Feb 2026 — for the stat tile only, use the /approvals/counts result which
  // filters by what THIS user can actually decide (matches Approvals inbox).
  const openReviewsCount = Number(approvalCounts?.pending?.review || 0);

  // --- Upcoming milestones — most overdue FIRST (then earliest due) ---
  const upcomingMilestones = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return allMilestones
      .filter((m) => m.status !== "done")
      .map((m) => ({ ...m, _due: m.due_date || "9999-12-31", _overdue: (m.due_date || "9999") < todayIso }))
      .sort((a, b) => {
        // overdue first, then by earliest due date
        if (a._overdue !== b._overdue) return a._overdue ? -1 : 1;
        return a._due.localeCompare(b._due);
      })
      .slice(0, 6);
  }, [allMilestones]);

  // --- Approvals waiting on me — Phase 67 (iter51): fetch the unified
  // /api/approvals/inbox feed so the Needs-you widget mirrors the Approvals
  // page kind coverage (10 kinds) and supports per-kind filtering. The legacy
  // hand-rolled `approvals` useMemo only knew leave/purchase/review.
  const [needsYouInbox, setNeedsYouInbox] = useState([]);
  const [needsYouKind, setNeedsYouKind] = useState("all");
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api.get("/approvals/inbox")
      .then(({ data }) => { if (alive) setNeedsYouInbox(data?.items || []); })
      .catch(() => { /* keep widget useful even if endpoint hiccups. */ });
    return () => { alive = false; };
  }, [user]);

  // Per-kind counts power the chip strip and let users see at a glance
  // where the backlog sits before they click in.
  const needsYouByKind = useMemo(() => {
    const m = {};
    needsYouInbox.forEach((it) => { m[it.kind] = (m[it.kind] || 0) + 1; });
    return m;
  }, [needsYouInbox]);

  // Filtered slice the widget actually renders (cap at 6 to keep the card lean).
  const approvals = useMemo(() => {
    const filtered = needsYouKind === "all"
      ? needsYouInbox
      : needsYouInbox.filter((it) => it.kind === needsYouKind);
    return filtered.slice(0, 6).map((it) => ({
      kind: it.kind,
      title: it.title,
      hint: it.subtitle || it.user_name || "",
      // Phase 79 — purchase items must land on the Approvals page (not the
      // Purchase Requests page). From Approvals, if more detail is needed
      // the user drills through to /studio?tab=purchases via the entity link.
      to: it.kind === "purchase" ? "/approvals?type=purchase" : (it.link || "/approvals"),
      _at: it.created_at,
    }));
  }, [needsYouInbox, needsYouKind]);

  // --- Open review tasks (Reports section) — most overdue FIRST ---
  const overdueOpenTasks = useMemo(() => {
    return [...openTasks].sort((a, b) => (a.due_date || a.created_at || "9999").localeCompare(b.due_date || b.created_at || "9999"));
  }, [openTasks]);

  // --- Recent active projects (top 4 the user is on) ---
  const myProjects = useMemo(() => {
    const inTeam = (p) => isAdmin || (p.team_ids || []).includes(user?.id) || p.client_manager_id === user?.id;
    return projects.filter((p) => p.status === "active").filter(inTeam).slice(0, 4);
  }, [projects, user?.id, isAdmin]);

  const todaysGoals = goals.slice(0, 4);
  const topAchievements = achievements.slice(0, 3);

  const checkedIn = !!today?.check_in;
  const checkedOut = !!today?.check_out;
  const minutesToday = today?.minutes_logged ?? 0;
  const eligibleCheckout = minutesToday >= 480;

  // ── Phase 46 — Finance tab data ─────────────────────────────────────
  // Lazy-load the first time Finance tab is opened (and only if the user has
  // finance permission, to keep zero noise for ICs).
  // Oct 2026 — server-side windowing: only the selected month (or YTD) is
  // fetched; "All time" fetches everything. The Outstanding snapshot tile is
  // NOT period-filtered, so it comes from a tiny status-only query.
  const canFinance = can("finance");
  useEffect(() => {
    if (activeTab !== "finance" || !canFinance) return;
    let cancelled = false;
    const win = monthWindow(financeMonth);
    const params = win ? { from: win.from, to: win.to } : {};
    Promise.all([
      api.get("/finance/invoices", { params }).catch(() => ({ data: [] })),
      api.get("/finance/expenses", { params }).catch(() => ({ data: [] })),
      api.get("/finance/invoices", { params: { status: "sent,overdue" } }).catch(() => ({ data: [] })),
      financeLoaded ? Promise.resolve({ data: runway }) : api.get("/finance/runway").catch(() => ({ data: null })),
    ]).then(([inv, exp, out, rw]) => {
      if (cancelled) return;
      setInvoices(inv.data || []);
      setExpenses(exp.data || []);
      setOutstandingInvoices(out.data || []);
      setRunway(rw.data);
      setFinanceLoaded(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canFinance, financeMonth]);

  // Phase 94 — MoM Navigator: a single Month Selector (All time or one
  // calendar month) now drives the summary tiles + both BI charts below,
  // computed entirely client-side from the already-fetched invoices/expenses
  // (fast, no extra round-trip per month change). Strict revenue recognition
  // (via the shared `invoiceRevenueINR` helper — Draft/Sent invoices count
  // as ₹0, only Received/Paid count) and Accrual Date (falls back to Date)
  // is the single source of truth for every time filter.
  const financeSummary = useMemo(() => {
    const revenue  = invoices.filter((i) => matchesMonth(i, financeMonth)).reduce((s, i) => s + invoiceRevenueINR(i), 0);
    const expense  = expenses.filter((e) => matchesMonth(e, financeMonth) && isCountableExpense(e)).reduce((s, e) => s + expenseAmountINR(e), 0);
    return { revenue, expense, net: revenue - expense };
  }, [invoices, expenses, financeMonth]);

  // Snapshot tile (NOT period-filtered) — fetched separately above.

  // Phase 94 — Cash Flow, Revenue by Service and Most Expensive Project
  // charts were removed from Home in Phase 122 — those analytics now live
  // in the new Finance Ledger Analytics Panel (pages/Finance.jsx).
  // ── Phase 46 — People tab tiny computed widgets ─────────────────────
  const peopleStats = useMemo(() => {
    // Most-leave-consumed (by user, this calendar year).
    const yearStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
    const yearLeaves = leaves.filter((l) => l.status === "approved" && (l.start_date || "") >= yearStart);
    const byUser = new Map();
    yearLeaves.forEach((l) => {
      const k = l.user_id;
      const cur = byUser.get(k) || { name: l.user_name, days: 0 };
      cur.days += Number(l.total_days || l.days || 1);
      byUser.set(k, cur);
    });
    const mostLeaveConsumed = Array.from(byUser.values()).sort((a, b) => b.days - a.days).slice(0, 5);
    return { mostLeaveConsumed };
  }, [leaves]);

  // Phase 84.62 — "Unlogged hours this month" + "Who's logged in" only
  // matter once the People tab is open (same lazy pattern as Finance).
  const [unloggedHours, setUnloggedHours] = useState(null);
  const [whosLoggedIn, setWhosLoggedIn] = useState(null);
  const [peopleExtrasLoaded, setPeopleExtrasLoaded] = useState(false);
  useEffect(() => {
    if (activeTab !== "people" || peopleExtrasLoaded) return;
    Promise.all([
      api.get("/dashboard/unlogged-hours").catch(() => ({ data: null })),
      api.get("/dashboard/whos-logged-in").catch(() => ({ data: null })),
    ]).then(([uh, wl]) => {
      setUnloggedHours(uh.data);
      setWhosLoggedIn(wl.data);
      setPeopleExtrasLoaded(true);
    });
  }, [activeTab, peopleExtrasLoaded]);

  // Phase 93 — Monthly Late Count (check-ins after 10:30 AM), founder/admin only.
  useEffect(() => {
    if (activeTab !== "people" || lateCountsLoaded || !canSeeAttendanceInsights) return;
    api.get("/attendance/insights").then(({ data }) => {
      setLateCounts(data?.team_distribution || []);
      setLateCountsLoaded(true);
    }).catch(() => setLateCountsLoaded(true));
  }, [activeTab, lateCountsLoaded, canSeeAttendanceInsights]);

  // Phase 84.63 — Project alerts (red banner): projects already past their
  // deadline with any Launch Gate still open. Founder/Admin only.
  const [projectAlerts, setProjectAlerts] = useState([]);
  useEffect(() => {
    if (!can("projects_edit")) return;
    api.get("/project-insights").then(({ data }) => {
      const rows = (data?.by_project || []).filter((p) => p.overdue && p.launch_gates_open > 0);
      setProjectAlerts(rows);
    }).catch(() => {});
  }, [can]);

  return (
    <div className="p-4 sm:p-8 max-w-[1600px] mx-auto fade-in" data-testid="dashboard-page">
      {/* Celebratory hero — UNCHANGED (per spec) */}
      {celebrations?.hero ? (
        <CelebrationHero hero={celebrations.hero} allCelebrations={celebrations} />
      ) : (
        <TodayBanner
          name={user?.name}
          subtitle={isAdmin ? "Studio-wide command center — every block links into the deeper hub." : "Your daily workspace. Tap any card to dive in."}
          dateLabel={bigDateLabel()}
          dateSubheading="What's on your plate right now"
        />
      )}

      {/* Phase 46 — sticky segmented control directly under the hero.
          Phase 105 — flush with the page container (no edge-bleed).
          Phase 116 — Check in/out + Log time moved here (compact).
          Phase 137 — sits directly on the page's light-grey background.
          Phase 144/146 — the Overview tab's Master Filter Bar + Metric Grid
          + Quick Add row live INSIDE this same sticky wrapper.
          Phase 147 — extracted verbatim into DashboardStickyHeader.jsx. */}
      <DashboardStickyHeader
        activeTab={activeTab} setActiveTab={setActiveTab} canFinance={canFinance} nav={nav}
        checkedIn={checkedIn} checkedOut={checkedOut} eligibleCheckout={eligibleCheckout} minutesToday={minutesToday}
        checkIn={checkIn} checkOut={checkOut} undoCheckOut={undoCheckOut}
        isFounder={isFounder} gratitudeToday={gratitudeToday} submitGratitude={submitGratitude}
        linkedinToday={linkedinToday} reloadLinkedin={reloadLinkedin}
        team={team.filter(isInternalStaff)} projects={projects} currentUserId={user?.id}
        taskPersonId={taskPersonId} setTaskPersonId={setTaskPersonId}
        taskProjectId={taskProjectId} setTaskProjectId={setTaskProjectId}
        taskBusinessUnit={taskBusinessUnit} setTaskBusinessUnit={setTaskBusinessUnit}
        taskStatusFilter={taskStatusFilter} setTaskStatusFilter={setTaskStatusFilter}
      />

      <DashboardOverviewTab
        activeTab={activeTab}
        balance={balance}
        taskStatusFilter={taskStatusFilter} setTaskStatusFilter={setTaskStatusFilter}
        taskPersonId={taskPersonId} taskProjectId={taskProjectId} taskBusinessUnit={taskBusinessUnit}
        socialTabSignal={socialTabSignal}
      />

      <DashboardPeopleTab
        activeTab={activeTab} team={team} isInternalStaff={isInternalStaff}
        pendingLeaves={pendingLeaves} unloggedHours={unloggedHours} canFinance={canFinance}
        canSeeAllLeaveBalances={canSeeAllLeaveBalances} allBalances={allBalances} balance={balance}
        whosLoggedIn={whosLoggedIn} canSeeAttendanceInsights={canSeeAttendanceInsights}
        checkedIn={checkedIn} checkedOut={checkedOut} minutesToday={minutesToday}
        lateCounts={lateCounts}
      />

      <DashboardProjectsTab
        activeTab={activeTab} activeProjects={activeProjects} openMilestones={openMilestones}
        openReviewsCount={openReviewsCount} goals={goals} projectAlerts={projectAlerts}
        needsYouInbox={needsYouInbox} needsYouKind={needsYouKind} setNeedsYouKind={setNeedsYouKind}
        needsYouByKind={needsYouByKind} approvals={approvals} overdueOpenTasks={overdueOpenTasks}
        upcomingMilestones={upcomingMilestones} projects={projects} myProjects={myProjects}
        milestonesByProject={milestonesByProject} canWeeklyReports={can("weekly_reports")}
        canPersonalGoals={can("personal_goals")} todaysGoals={todaysGoals} topAchievements={topAchievements}
      />

      <DashboardFinanceTab
        activeTab={activeTab} canFinance={canFinance} runway={runway}
        financeMonth={financeMonth} setFinanceMonth={setFinanceMonth}
        financeSummary={financeSummary} outstandingInvoices={outstandingInvoices}
        pendingPurchases={pendingPurchases}
      />

      <TabSection activeTab={activeTab} id="topposts">
        <TopPostsFeed limit={18} />
      </TabSection>

      <TabSection activeTab={activeTab} id="inspiration">
        <InspirationFeed onPostCreated={handleInspirationPostCreated} />
      </TabSection>

      <StatCard label="Pro tip" value="Tap any tile or card to jump into its hub" hint="Home is the fast surface — hubs are where you do the work." />
    </div>
  );
}
